import React from "react";
import { Reveal, Overline } from "@/components/Reveal";
import { PageHero, Section, Button } from "@/components/UI";
import { Check } from "lucide-react";

const STEPS = [
  { n: "01", t: "Eligibility & Prerequisites", d: "Foundational training, health-and-safety certification, and documented supervised practice hours in restorative micropigmentation." },
  { n: "02", t: "Standards Examination", d: "A written and practical assessment against the national standards of practice, ethics, and infection prevention and control." },
  { n: "03", t: "Portfolio Review", d: "Submission of restorative case studies demonstrating competency across relevant applications and trauma-informed care." },
  { n: "04", t: "Certification Granted", d: "Successful candidates are certified and listed in the public national registry with verified status." },
  { n: "05", t: "Continuing Competency", d: "Annual education credits, case review, and adherence to the code of ethics to maintain standing." },
];

const REQS = [
  "Recognized foundational micropigmentation training",
  "Current bloodborne pathogens / IPAC certification",
  "Documented supervised restorative practice hours",
  "First aid & CPR certification",
  "Professional liability insurance",
  "Signed adherence to the CCPM Code of Ethics",
];

export default function Certification() {
  return (
    <>
      <PageHero overline="Certification" testId="certification-hero"
        title="The path to national certification"
        subtitle="CCPM's national certification framework is in active development. Below is the proposed pathway a restorative practitioner will follow to become certified once the programme launches — shared now for transparency and consultation." />

      <Section className="py-20 lg:py-28">
        <Reveal><Overline>Proposed Pathway</Overline><h2 className="font-serif text-4xl lg:text-5xl text-ccpm-charcoal mt-4 max-w-2xl leading-[1.05]">Five stages, from application to certification</h2></Reveal>
        <div className="mt-14 border-t border-ccpm-border">
          {STEPS.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="group grid md:grid-cols-12 gap-6 py-9 border-b border-ccpm-border items-start hover:bg-ccpm-stone/40 transition-colors px-2">
                <div className="md:col-span-2 font-serif text-5xl text-ccpm-olive/40">{s.n}</div>
                <h3 className="md:col-span-4 font-serif text-2xl lg:text-3xl text-ccpm-charcoal">{s.t}</h3>
                <p className="md:col-span-6 text-ccpm-taupe font-light leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <div className="bg-ccpm-charcoal text-ccpm-ivory py-20 lg:py-28">
        <Section>
          <div className="grid lg:grid-cols-2 gap-14">
            <Reveal>
              <Overline className="!text-ccpm-olive-soft">Anticipated Requirements</Overline>
              <h2 className="font-serif text-4xl text-ccpm-ivory mt-4 leading-tight">What certification will likely require</h2>
              <p className="mt-5 text-white/60 font-light leading-relaxed">These proposed requirements are being finalized as part of our national standard. Register your interest to be notified when certification opens for applications.</p>
              <Button to="/registry/apply" variant="light" className="mt-8" data-testid="certification-apply">Register your interest</Button>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="space-y-4">
                {REQS.map((r, i) => (
                  <li key={i} className="flex items-start gap-4 border-b border-white/10 pb-4">
                    <Check size={20} strokeWidth={1.5} className="text-ccpm-olive-soft shrink-0 mt-0.5" />
                    <span className="text-ccpm-ivory/90 font-light">{r}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </Section>
      </div>
    </>
  );
}
